import React from 'react';
import DefaultLayout from '../../../layouts/DefaultLayout';
import TemplateColumn, { TemplateColumnWithSidebarContainer } from '../../../layouts/TemplateColumn';
import { PageHeader, PageSection, RelatedContent, CrossLink, IndicationPageIntro, AnchorLinks, TextBlock, DataTable  } from '../../../components';
import GlobalSafetyData, { globalSafetyBlock } from './_global_safety_data'
import { jobCodes } from '../../../util/data/jobCodes';
import ar1MtnbcTable1 from '../../../assets/MTNBC_AR1_S.svg';
import ar2MtnbcTable2 from '../../../assets/MTNBC_AR2_S.svg';
import { StaticImage } from 'gatsby-plugin-image';

// SEO INFORMATION 
const pageMeta = {
    title: `KEYNOTE-D19 - Adverse Reactions & Safety Data`,
    keywords: `keywords`,
    description: `Learn about select adverse reactions to KEYTRUDA® (pembrolizumab) in patients with unresectable locally advanced or metastatic TNBC whose tumors express PD-L1 (CPS ≥10). See selected adverse reactions in KEYNOTE-D19.`,
    schemaJsonLD: [
    `{"@context":"https://schema.org/","url":"https://www.keytrudahcp.com/safety/adverse-reactions/metastatic-triple-negative-breast-cancer/","@type":"MedicalAudience","audienceType":"US Health Care Professionals","geographicArea":"This site is intended only for residents of the United States and its territories, including Puerto Rico."}`,
    `{"@context":"https://schema.org/","url":"https://www.keytrudahcp.com/safety/adverse-reactions/metastatic-triple-negative-breast-cancer/","@type":"MedicalWebPage","name":"Selected Adverse Reactions in KEYNOTE-D19 for KEYTRUDA","description":"The safety investigation of KEYNOTE-D19 included 221 patients who received at least one dose of KEYTRUDA in combination with sacituzumab govitecan-hziy (SG-hziy)"}`
    ]
}

//from /src/util/data/indications.js
// UPDATE THIS TO THE CORRECT INDICATION #
const indicationId = 42;
const jobCode = jobCodes[26].jobCode;

const relatedContent = [
    {
        section: 'Resources',
        link: {
            url: '/resources/mechanism-of-action/',
            label: 'Mechanism of Action'
        }
    },
    {
        section: 'Resources',
        link: {
            url: 'https://www.merckaccessprogram-keytruda.com/',
            label: 'Access & Reimbursement Support'
        }
    }
];

const crossLinkContent = {
    sections : [
        {
            heading: "Additional Clinical Data for KEYTRUDA ",
            items: [
                { text: 'High-Risk Early-Stage TNBC', url: '/efficacy/early-stage-triple-negative-breast-cancer/' },
                { text: 'Combo therapy – Locally Recurrent Unresectable or Metastatic TNBC', url: '/efficacy/triple-negative-breast-cancer/' },
                ]
        },
        {
            heading: "Related Information",
            items: [
                { text: 'Treatment Monitoring & Management', url: '/safety/monitoring-managing-adverse-reactions/' },
                { text: 'Dose Modifications', url: '/dosing/dose-modifications/' },
                ]
        },
    ],
    interestLinks : {
        heading: "You Might Be Interested In",
        items: [
            { eyebrow: 'Resources', text: 'Mechanism of Action', url: '/resources/mechanism-of-action/' },
        ],
    }
};

const anchorLinkModelData = [
    {label: 'Incidence of Selected Immune-Mediated Adverse Reactions'},
    {label: 'Management of Selected Immune-Mediated Adverse Reactions'},
    {label: 'Selected Adverse Reactions in KEYNOTE&#8288;-&#8288;D19 for KEYTRUDA'}
]

const ar2Footnotes = [
    {   label: 'a.', 
        text: 'Chemotherapy: paclitaxel, nab-paclitaxel, or gemcitabine and carboplatin. '
    }
]

const ar2Definitions = 'ALT = alanine aminotransferase; AST = aspartate aminotransferase; LDH = lactate dehydrogenase.'

const Page = ({ location }) => {

    return (
        <DefaultLayout indicationId={indicationId} pageMeta={ pageMeta } location={ location } jobCode={jobCode}>
            <div data-template-name="template-e">
                {/* Begin Intro Column */}
                <TemplateColumn id="template-e-intro" maxWidth={824}>
                    <PageHeader batch2={true} isBranding={true} title="Selected Adverse Reactions" />
                   <PageSection bgColor="white">
                        <IndicationPageIntro indicationId={indicationId} hideBridgingStatement={true} />
                   </PageSection>
                </TemplateColumn>
                {/* End Intro Column */}

                <TemplateColumnWithSidebarContainer>
                {/* Begin Main Column */}
                    <TemplateColumn id="template-e-main">
                        <PageSection bgColor="cloud" title="Severe and Fatal Immune-Mediated Adverse Reactions">
                            <TextBlock>{globalSafetyBlock}</TextBlock>
                            <AnchorLinks items={anchorLinkModelData} />
                        </PageSection>

                        <GlobalSafetyData />

                        <PageSection title={anchorLinkModelData[2].label}>
                            <TextBlock>
                                <p>The safety investigation of KEYNOTE-D19 included 221 patients who received at least one dose of KEYTRUDA in combination with sacituzumab govitecan-hziy (SG-hziy)</p>
                                <p>Among patients who received KEYTRUDA + SG-hziy, the median duration of exposure to KEYTRUDA was 8.5 months (range: 1 day to 26.8 months).</p>
                                <p>Fatal adverse reactions occurred in 3.2% of patients receiving KEYTRUDA in combination with SG-hziy, including death due to unknown cause (0.9%) and 0.5% of each of the following:
                                    <ul>
                                            <ul>
                                                <li>Completed suicide</li>
                                                <li>Neutropenic sepsis </li>
                                                <li>Sepsis</li>
                                                <li>Pneumonia</li>
                                                <li>Pulmonary embolism</li>
                                            </ul>
                                    </ul>
                                </p>
                                <p>Serious adverse reactions occurred in 38% of patients receiving KEYTRUDA in combination with SG-hziy. Those occurring in ≥2% of patients were:
                                    <ul>
                                        <li>Febrile neutropenia (7%)</li>
                                        <li>Neutropenia (6%)</li>
                                        <li>Diarrhea (5%)</li>
                                        <li>Fatigue (2.3%)</li>
                                        <li>Pneumonia (2.3%)</li>
                                    </ul>
                                </p>
                                <p>Permanent discontinuation of KEYTRUDA, due to an adverse reaction occurred in 9% of patients. The most common adverse reactions resulting in permanent discontinuation of KEYTRUDA (≥1%) were pneumonitis and rash (1.4% each).</p>
                                     
                            </TextBlock>

                            <DataTable
                                title="Dose interruptions of KEYTRUDA due to adverse reactions occurred in 67% of patients. The most common adverse reactions leading to interruption of KEYTRUDA (≥2%) were:" 
                                mobileImage={() => <img src={ar1MtnbcTable1} style={{width:'100%', maxWidth:'500px', margin: '0 auto'}} alt='KEYNOTE-D19: The Common Adverse Reactions Leading to Interruption of KEYTRUDA® (pembrolizumab) (≥2%)' />}
                                desktopImage={() => <StaticImage quality={90} placeholder="DOMINANT_COLOR" src="../../../assets/MTNBC_AR1_XL.png" alt='KEYNOTE-D19: The Common Adverse Reactions Leading to Interruption of KEYTRUDA® (pembrolizumab) (≥2%)' />}
                            />
                            <DataTable
                                title="The most common (≥25%) adverse reactions, including laboratory abnormalities, occurring in patients treated with KEYTRUDA in combination with SG-hziy were:" 
                                mobileImage={() => <img src={ar2MtnbcTable2} style={{width:'100%', maxWidth:'500px', margin: '0 auto'}} alt='KEYNOTE-D19: The Most Common (≥25%) Adverse Reactions, Including Laboratory Abnormalities' />}
                                desktopImage={() => <StaticImage quality={90} placeholder="DOMINANT_COLOR" src="../../../assets/MTNBC_AR2_XL.png" alt='KEYNOTE-D19: The Most Common (≥25%) Adverse Reactions, Including Laboratory Abnormalities' />}
                                definitions= {ar2Definitions}
                                footnotes= {ar2Footnotes}
                            />

                        </PageSection>
                    </TemplateColumn>
                    {/* End Main Column */}
                    
                    <TemplateColumn id="template-e-sidebar">
                        <PageSection bgColor="mist">
                            <CrossLink {...crossLinkContent} />
                        </PageSection>
                    </TemplateColumn>

                </TemplateColumnWithSidebarContainer>

                <TemplateColumn id="template-e-secondary">
                    <PageSection bgColor="keytrudaGreen">
                        <RelatedContent items={relatedContent}/>
                    </PageSection>
                </TemplateColumn>
            </div>
        </DefaultLayout>
    );
};

export default Page;
